import React, { useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Camera, ImagePlus, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "../utils/imageCompress";
import { PHOTO_CATEGORIES } from "../constants/propertyOptions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fotoğraf yükleyici — arka planda paralel yükleme, ilerleme göstergesi,
 * mobil kamera açma (capture="environment"), kategori tag, ana foto seçimi.
 */
const PhotoUploader = ({ propertyId, photos = [], onChange }) => {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [uploading, setUploading] = useState([]); // [{tmpId, name, progress}]

  const handleFiles = async (files, defaultCategory = "Diğer") => {
    if (!propertyId) {
      toast.error("Önce ilanı kaydedin, sonra fotoğraf ekleyebilirsiniz");
      return;
    }
    const list = Array.from(files);
    if (list.length === 0) return;

    for (const original of list) {
      const tmpId = `tmp-${Date.now()}-${Math.random()}`;
      setUploading((u) => [...u, { tmpId, name: original.name, progress: 0 }]);
      try {
        const compressed = await compressImage(original);
        const form = new FormData();
        form.append("file", compressed);
        form.append("category", defaultCategory);
        form.append("is_main", photos.length === 0 && list.indexOf(original) === 0 ? "true" : "false");

        const { data } = await axios.post(
          `${API_URL}/api/properties/${propertyId}/photos`,
          form,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (evt.total) {
                const pct = Math.round((evt.loaded / evt.total) * 100);
                setUploading((u) => u.map(x => x.tmpId === tmpId ? { ...x, progress: pct } : x));
              }
            },
          }
        );
        onChange?.(data);
      } catch (e) {
        console.error("Photo upload error:", e);
        toast.error(`Yüklenemedi: ${original.name}`);
      } finally {
        setUploading((u) => u.filter(x => x.tmpId !== tmpId));
      }
    }
    toast.success("Fotoğraflar yüklendi");
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(
        `${API_URL}/api/properties/${propertyId}/photos/${photoId}`,
        { withCredentials: true }
      );
      onChange?.({ __refresh: true });
      toast.success("Fotoğraf silindi");
    } catch (e) {
      toast.error("Silme başarısız");
    }
  };

  const updatePhoto = async (photoId, payload) => {
    try {
      await axios.patch(
        `${API_URL}/api/properties/${propertyId}/photos/${photoId}`,
        null,
        { params: payload, withCredentials: true }
      );
      onChange?.({ __refresh: true });
    } catch (e) {
      toast.error("Güncelleme başarısız");
    }
  };

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          data-testid="photo-camera-button"
          onClick={() => cameraInputRef.current?.click()}
          className="bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:hover:bg-[#E4E4E7]"
        >
          <Camera className="w-4 h-4 mr-2" />
          Fotoğraf Çek
        </Button>
        <Button
          type="button"
          data-testid="photo-gallery-button"
          onClick={() => galleryInputRef.current?.click()}
          variant="outline"
          className="border-[#E4E4E7] dark:border-[#27272A]"
        >
          <ImagePlus className="w-4 h-4 mr-2" />
          Galeriden Seç
        </Button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Uploading */}
      {uploading.length > 0 && (
        <div className="space-y-2">
          {uploading.map((u) => (
            <div
              key={u.tmpId}
              data-testid="photo-uploading-row"
              className="flex items-center gap-3 p-3 bg-[#F9FAFB] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#27272A] rounded"
            >
              <Loader2 className="w-4 h-4 text-[#F59E0B] animate-spin flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#0A0A0A] dark:text-[#FAFAFA] truncate">
                  {u.name}
                </div>
                <div className="h-1 bg-[#E4E4E7] dark:bg-[#27272A] rounded mt-1">
                  <div
                    className="h-full bg-[#F59E0B] rounded transition-all duration-200"
                    style={{ width: `${u.progress}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                {u.progress}%
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Existing photos */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((photo) => (
            <div
              key={photo.id}
              data-testid="photo-thumb"
              className={`relative rounded-lg overflow-hidden border-2 group ${
                photo.is_main
                  ? "border-[#F59E0B] shadow-md"
                  : "border-[#E4E4E7] dark:border-[#27272A]"
              }`}
            >
              <img
                src={`${API_URL}/api/photos/${photo.id}`}
                alt={photo.category}
                className="w-full h-32 object-cover"
                loading="lazy"
              />
              {photo.is_main && (
                <div className="absolute top-1 left-1 bg-[#F59E0B] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" /> ANA
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                <Select
                  value={photo.category}
                  onValueChange={(v) => updatePhoto(photo.id, { category: v })}
                >
                  <SelectTrigger
                    className="h-7 bg-white/90 dark:bg-black/70 text-xs border-none text-[#0A0A0A] dark:text-[#FAFAFA]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHOTO_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-1 mt-1">
                  {!photo.is_main && (
                    <button
                      onClick={() => updatePhoto(photo.id, { is_main: true })}
                      className="flex-1 text-[10px] bg-white/90 hover:bg-white text-[#0A0A0A] py-1 rounded"
                    >
                      Ana Yap
                    </button>
                  )}
                  <button
                    onClick={() => deletePhoto(photo.id)}
                    className="text-[10px] bg-red-500/90 hover:bg-red-600 text-white px-2 py-1 rounded"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && uploading.length === 0 && (
        <div className="text-center py-6 text-xs text-[#52525B] dark:text-[#A1A1AA]">
          Henüz fotoğraf yok. Kamera veya galeriden ekleyin.
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
